// Description: Input that allow the user to write a code received in his e-mail to verify the email
// Updated: 11/07/2025
// Author: Vitor de Jesus

// OTP.jsx
import React from 'react';
import { Box as MuiBox } from '@mui/material';
import { Input as BaseInput } from '@mui/base/Input';
import { styled } from '@mui/material/styles';

const InputElement = styled('input')(({ theme }) => `
  width: 60px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 1.25rem;
  padding: 12px;
  border-radius: 8px;
  text-align: center;
  color: ${theme.palette.mode === 'dark' ? '#fff' : '#000'};
  background: ${theme.palette.mode === 'dark' ? '#1C2025' : '#fff'};
  border: 1px solid ${theme.palette.mode === 'dark' ? '#434D5B' : '#ccc'};
  &:focus {
    border-color: ${theme.palette.mode === 'dark' ? '#A986FF' : '#7121E0'};
    box-shadow: 0 0 0 3px ${theme.palette.mode === 'dark' ? '#5F1BBC' : '#8033F4'};
    outline: none;
  }
`);

const OTP = ({
    separator,
    length,
    value,
    onChange,
    onComplete,
    focusedIndex,
    setFocusedIndex,
}) => {
    const inputRefs = React.useRef([]);

    const focusInput = (targetIndex) => {
        const targetInput = inputRefs.current[targetIndex];
        targetInput?.focus();
    };

    const selectInput = (targetIndex) => {
        const targetInput = inputRefs.current[targetIndex];
        targetInput?.select();
    };

    const handleKeyDown = (event, currentIndex) => {
        switch (event.key) {
            case 'ArrowLeft':
                event.preventDefault();
                if (currentIndex > 0) {
                    focusInput(currentIndex - 1);
                    selectInput(currentIndex - 1);
                    setFocusedIndex(currentIndex - 1);
                }
                break;
            case 'ArrowRight':
                event.preventDefault();
                if (currentIndex < length - 1) {
                    focusInput(currentIndex + 1);
                    selectInput(currentIndex + 1);
                    setFocusedIndex(currentIndex + 1);
                }
                break;
            case 'Backspace':
            case 'Delete':
                event.preventDefault();
                onChange((prev) => {
                    const otp = prev.slice(0, currentIndex) + '' + prev.slice(currentIndex + 1);
                    return otp;
                });
                if (event.key === 'Backspace' && currentIndex > 0) {
                    focusInput(currentIndex - 1);
                    selectInput(currentIndex - 1);
                    setFocusedIndex(currentIndex - 1);
                }
                break;
        }
    };

    const handleChange = (event) => {
        const input = event.target.value;
        const valueToInsert = input.slice(-1);

        onChange((prev) => {
            const otpArray = prev.split('');
            otpArray[focusedIndex] = valueToInsert;
            const newValue = otpArray.join('');

            if (newValue.replace(/\s/g, '').length === length) {
                onComplete(newValue);
            }

            return newValue;
        });

        if (valueToInsert && focusedIndex < length - 1) {
            focusInput(focusedIndex + 1);
            setFocusedIndex(focusedIndex + 1);
        }
    };

    const handlePaste = (event) => {
        event.preventDefault();
        const paste = (event.clipboardData || window.clipboardData).getData('text');
        const cleanPaste = paste.trim().slice(0, length);

        if (!cleanPaste) return;

        const updatedValue = cleanPaste
            .padEnd(length, ' ') 
            .split('')
            .join('');

        onChange(() => updatedValue);

        if (cleanPaste.length === length) {
            onComplete(updatedValue);
        }

        const lastIndex = Math.min(cleanPaste.length - 1, length - 1);
        setFocusedIndex(lastIndex);
        focusInput(lastIndex);
    };

    return (
        <MuiBox sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {Array.from({ length }).map((_, index) => (
                <React.Fragment key={index}>
                    <BaseInput
                        slots={{ input: InputElement }}
                        slotProps={{
                            input: {
                                ref: (el) => { inputRefs.current[index] = el; },
                                onKeyDown: (e) => handleKeyDown(e, index),
                                onChange: handleChange,
                                onFocus: () => setFocusedIndex(index),
                                onPaste: handlePaste,
                                value: value[index] ?? '',
                                disabled: value.length === length,
                            },
                        }}
                        aria-label={`Digit ${index + 1} of OTP`}
                    />
                    {index !== length - 1 && separator}
                </React.Fragment>
            ))}
        </MuiBox>
    );
};

export default OTP;
